import * as React from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { theme } from 'lib/style';
import {
  Dropdown,
  LoadingIndicator,
  NewModal,
  TextInput,
} from 'lib/components';
import {
  IntegrationCustomer,
  integrations,
  VinsolutionDealer,
  INTEGRATIONS_TYPE,
} from '../../../app/pages/admin/integrations/Main';
import { MdPeople } from 'react-icons/md';
import { CRMAssignUserModal } from './CRMAssignUserModal';
import {
  connectELead,
  connectVinSolution,
  connectDealersocket,
} from '../../api/crmIntegrationsApi';
import Switch from '../../../app/pages/design/landingPageBuilder/components/Switch';
import { successToast } from '../toasts/success';
import { Button } from 'react-covideo-common';
import { useGetVinsolutionUsersQuery } from 'lib/api/crmIntegrations/getVinsolutionUsers';
import { Flex, Gap } from '../styles/layout';
import { useLeadNotificationsMutation } from 'lib/api/crm/useLeadNotificationsMutation';
import { useToggleVinSolutionsConnectEvent } from 'lib/api/crm/useToggleVinSolutionsConnectEvent';
import { InfoMessage } from 'app/pages/library/components/boardModal/style';
import { ParagraphNormalBold500 } from '../styles/typography';

const AuthorizeWrapper = styled.div`
  margin-right: auto;
  display: flex;
  flex-direction: row;
  gap: 6px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 32px 0;
  margin-bottom: auto;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.div`
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: ${theme.palette.label};
  margin-bottom: 8px;
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 400px;
  margin-top: auto;
  margin-bottom: auto;
`;

const CustomLabel = (option: { name: string; dealerId: number }) => {
  return (
    <Flex justifyContent='space-between' width='calc(100% - 6px)'>
      <ParagraphNormalBold500 style={{ fontSize: '15px', color: 'inherit' }}>
        {option.name} - {option.dealerId}
      </ParagraphNormalBold500>
    </Flex>
  );
};

type Props = {
  handleModalClose: () => void;
  onSubmit: () => void;
  customer: IntegrationCustomer;
  vinsolutionDealers: VinsolutionDealer[];
  integrationType: number;
};

export const CRMIntegrationModal = ({
  handleModalClose,
  onSubmit,
  customer,
  vinsolutionDealers,
  integrationType,
}: Props) => {
  const themes = useTheme();
  const { customerId } = customer;

  const [assignUserModal, setAssignUserModal] = useState(false);
  const [dealerAdmin, setDealerAdmin] = useState(
    !!customer.vinSolutionsDealer
      ? customer.vinSolutionsDealer?.adminVinUserId
      : 0
  );

  const [selectedDealer, setSelectedDealer] = useState(
    !!customer.vinSolutionsDealer ? customer.vinSolutionsDealer : null
  );
  const [isEleadEnabled, setIsELeadEnabled] = useState(!!customer.eLeadEnabled);
  const [dealerSocketId, setDealerSocketId] = useState(
    !!customer.dealerSocketId
      ? `${customer.dealerSocketId}_${customer.dealerSocketFranchiseId}`
      : ''
  );

  const [customerLeadNotificationAccess, setCustomerLeadNotificationAccess] =
    useState(customer.leadsNotificationsAccess);
  const [vinConnectEnabled, setVinConnectEnabled] = useState(
    !!customer.vinSolutionsDealer?.vinsolutionsConnect
  );
  const [isLoading, setIsLoading] = useState(false);
  const {
    data: vinsolutionDealerUsers,
    isFetching: usersLoading,
    refetch,
  } = useGetVinsolutionUsersQuery(
    selectedDealer?.dealerId || 0,
    integrationType === INTEGRATIONS_TYPE.VINSOLUTIONS
  );

  const { mutateAsync: toggleLeadNotifications } =
    useLeadNotificationsMutation();

  const { mutateAsync: toggleVinSolutionsConnect } =
    useToggleVinSolutionsConnectEvent();

  const handleAssignUserModal = async () => {
    successToast({
      title: `User connected`,
    });
    setAssignUserModal(false);
    refetch();
  };

  const handleEleadIntegrationChange = () => {
    setIsELeadEnabled(!isEleadEnabled);
  };

  const handleEleadSubmit = async () => {
    await connectELead(
      {
        eleadEnabled: Number(isEleadEnabled),
      },
      customer.customerId
    ).catch(error => console.log(error));
    successToast({
      title: `${customer.business} connected with eLead Integration`,
    });
    onSubmit();
  };

  const handleVinsolutionConnect = async (showToast: boolean = true) => {
    setIsLoading(true);
    await connectVinSolution(
      {
        dealerId: selectedDealer?.dealerId,
        vinSolutionsEnabled: true,
        adminVinUserId: !!dealerAdmin ? dealerAdmin : null,
      },
      customer.customerId
    ).catch(error => console.log(error));
    setIsLoading(false);
    if (showToast) {
      successToast({
        title: `${customer.business} connected with dealer ID: ${selectedDealer?.dealerId}`,
      });
    }
  };

  const handleDealerSocketSubmit = async () => {
    const dealerSocket = dealerSocketId.split('_');
    if (dealerSocket.length !== 2) {
      return;
    }

    await connectDealersocket(
      customer.customerId,
      true,
      dealerSocket[0],
      dealerSocket[1]
    ).catch(error => console.log(error));
    successToast({
      title: `${customer.business} connected with Dealersocket Integration`,
    });
    onSubmit();
  };

  const switchToAssignUsersModal = async () => {
    setAssignUserModal(true);
    refetch();
  };

  const handleToggleLeadNotifications = async () => {
    // toggle value from state
    const newLeadsNotificationAccess = !!customerLeadNotificationAccess ? 0 : 1;
    setCustomerLeadNotificationAccess(newLeadsNotificationAccess);
    await toggleLeadNotifications({
      customerId,
      leadsNotificationsAccess: newLeadsNotificationAccess,
    });
  };

  const handleToggleVinConnectEvent = async () => {
    setVinConnectEnabled(true);
    try {
      await toggleVinSolutionsConnect(customerId);
    } catch (e) {
      setVinConnectEnabled(false);
    }
  };
  const filteredVinsolutionDealers = vinsolutionDealerUsers?.users?.filter(
    user => user.ilmAccess === 'Admin' && user.isActive === 1
  );

  return (
    <>
      {assignUserModal && (
        <CRMAssignUserModal
          handleModalClose={() => setAssignUserModal(false)}
          handleModalSave={handleAssignUserModal}
          customer={customer}
          vinsolutionDealerUsers={vinsolutionDealerUsers?.users || []}
        />
      )}
      {!assignUserModal && (
        <NewModal
          closeModalOnBackdropOrEscPress={true}
          closeModal={() => handleModalClose()}
          headerText={customer.business}
        >
          {usersLoading || isLoading ? (
            <div style={{ height: 350 }}>
              <LoadingIndicator isLoading={true} />
            </div>
          ) : (
            <div>
              <Row>
                <div style={{ flex: 1 }}>
                  <Label>CRM Integration</Label>
                  <Dropdown
                    placeholder={'Select CRM Integration'}
                    value={integrations.find(o => o.value == integrationType)}
                    options={integrations}
                    onChange={() => {}}
                    disabled={true}
                  />
                </div>
              </Row>
              {integrationType === INTEGRATIONS_TYPE.VINSOLUTIONS && (
                <Gap
                  style={{ marginTop: '16px' }}
                  flexDirection='column'
                  alignItems='flex-start'
                >
                  <Flex>
                    <Label>Dealership</Label>
                    <Dropdown
                      placeholder={'Select Dealership'}
                      options={vinsolutionDealers}
                      dropdownHeight={
                        vinsolutionDealers.length > 4 ? 220 : 'auto'
                      }
                      onChange={(option: VinsolutionDealer) => {
                        setSelectedDealer({
                          ...option,
                          value: option.dealerId,
                        });
                        setVinConnectEnabled(!!option.vinsolutionsConnect); // update vinconnect field
                      }}
                      getOptionValue={option => option.dealerId}
                      getOptionLabel={option => option.name}
                      value={vinsolutionDealers.find(
                        o => o.dealerId == selectedDealer?.dealerId
                      )}
                      formatOptionLabel={CustomLabel}
                    />
                  </Flex>
                  <Flex>
                    <Label>Lead Owner</Label>
                    <Dropdown
                      placeholder={'Select Lead Owner'}
                      options={filteredVinsolutionDealers}
                      dropdownHeight={
                        (filteredVinsolutionDealers || []).length > 4
                          ? 220
                          : 'auto'
                      }
                      menuPortalTarget={document.body}
                      onChange={option => {
                        setDealerAdmin(option.vinUserId);
                      }}
                      getOptionValue={option => option.vinUserId}
                      getOptionLabel={option =>
                        option.firstName + ' ' + option.lastName
                      }
                      value={filteredVinsolutionDealers?.find(
                        user => user.vinUserId == dealerAdmin
                      )}
                    />
                  </Flex>

                  <Flex
                    flexDirection='row'
                    width='250px'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <div>Enable lead notifications</div>
                    <Switch
                      id={'switch-lead-notifications'}
                      isOn={!!customerLeadNotificationAccess}
                      onColor={themes.colors.primary[100]}
                      handleToggle={handleToggleLeadNotifications}
                    />
                  </Flex>
                  <Flex>
                    <Flex
                      flexDirection='row'
                      justifyContent='space-between'
                      width='250px'
                    >
                      <div>Access to ConnectEvent</div>
                      <Switch
                        id={'switch-connectEvent'}
                        isOn={vinConnectEnabled}
                        onColor={themes.colors.primary[100]}
                        handleToggle={handleToggleVinConnectEvent}
                        disabled={vinConnectEnabled}
                      />
                    </Flex>
                    {vinConnectEnabled && (
                      <InfoMessage style={{ width: '100%' }}>
                        ConnectEvent service cannot be turned off.
                      </InfoMessage>
                    )}
                  </Flex>
                </Gap>
              )}

              {integrationType === INTEGRATIONS_TYPE.ELEAD && (
                <>
                  <Row>
                    <div
                      style={{
                        marginRight: 12,
                        marginTop: 30,
                        flex: 1,
                      }}
                    >
                      <ToggleContainer>
                        <Switch
                          id={'switch-integrations'}
                          isOn={isEleadEnabled}
                          onColor={themes.colors.primary[100]}
                          handleToggle={() => {
                            handleEleadIntegrationChange();
                          }}
                        />
                      </ToggleContainer>
                    </div>
                  </Row>
                  <Footer>
                    <Button
                      variant='primary'
                      text={'Save Changes'}
                      onClick={handleEleadSubmit}
                    />
                  </Footer>
                </>
              )}
              {integrationType === INTEGRATIONS_TYPE.VINSOLUTIONS && (
                <Footer>
                  <AuthorizeWrapper>
                    <Button
                      onClick={() => switchToAssignUsersModal()}
                      icon={<MdPeople />}
                      text={'Assign Users'}
                      variant='secondary'
                      disabled={!selectedDealer}
                    />
                  </AuthorizeWrapper>
                  <Button
                    onClick={async () => {
                      await handleVinsolutionConnect();
                      onSubmit();
                    }}
                    text={'Save Changes'}
                    type='submit'
                    disabled={!selectedDealer}
                    variant='primary'
                  />
                </Footer>
              )}
              {integrationType === INTEGRATIONS_TYPE.DEALERSOCKET && (
                <>
                  <Row>
                    <div
                      style={{
                        marginTop: 30,
                        flex: 1,
                      }}
                    >
                      <Label>DealerSocket ID</Label>

                      <TextInput
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setDealerSocketId(e.target.value);
                        }}
                        value={dealerSocketId}
                        margin={'0 0 32px 0'}
                        placeholder='DealerSocketID should be written in xxxx_x format'
                      />
                    </div>
                  </Row>
                  <Footer>
                    <Button
                      text={'Save Changes'}
                      onClick={handleDealerSocketSubmit}
                      variant='primary'
                    />
                  </Footer>
                </>
              )}
            </div>
          )}
        </NewModal>
      )}
    </>
  );
};
